import { LogOut, Sparkles } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/AuthProvider";
import { cn } from "@/lib/utils";

import { DensityToggle } from "./DensityToggle";
import { LanguageToggle } from "./LanguageToggle";
import { ThemeToggle } from "./ThemeToggle";

interface TopNavProps {
  className?: string;
  /** Phase 6a.9 — slot for the shadcn `SidebarTrigger` (mobile hamburger). */
  leftSlot?: React.ReactNode;
}

/**
 * TopNav — Phase 6a.2 (introduced) / 6a.9 (rebuild).
 *
 * 64px tall header. Branding (Logo + app name) lives in the Sidebar
 * (Phase 6a.9 design). The TopNav now carries only utility actions:
 * left = sidebar trigger; right = language toggle, theme toggle, AI
 * assistant button (disabled until Phase 10).
 */
export function TopNav({ className, leftSlot }: TopNavProps) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async (): Promise<void> => {
    try {
      await signOut();
      navigate("/login", { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Çıkış başarısız: ${message}`);
    }
  };

  return (
    <header
      className={cn(
        "flex h-16 items-center justify-between border-b bg-background px-4",
        className,
      )}
    >
      <div className="flex items-center gap-3">{leftSlot}</div>
      <div className="flex items-center gap-2">
        <DensityToggle />
        <LanguageToggle />

        <ThemeToggle />

        {/* TODO(Phase 10): wire AI Assistant panel */}
        <Button
          variant="ghost"
          size="icon"
          disabled
          aria-label={t("ai.assistantLabel")}
        >
          <Sparkles size={20} />
        </Button>

        {user?.email ? (
          <span className="hidden max-w-[180px] truncate text-sm text-muted-foreground md:inline">
            {user.email}
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          aria-label="Çıkış yap"
          title="Çıkış yap"
        >
          <LogOut size={20} />
        </Button>
      </div>
    </header>
  );
}
